import { useState, useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Menu, ChevronDown } from 'lucide-react';

const NAV_GROUPS = [
  {
    label: 'Foundations',
    href: '/foundations',
    children: [
      { href: '/work', label: 'Framework Library' },
      { href: '/essays', label: 'Essays' },
      { href: '/sacred-feminine-archetypes', label: 'Sacred Feminine Archetypes' },
    ]
  },
  {
    label: 'Guidance',
    href: '/guidance',
    children: [
      { href: '/idea-clarity-session', label: 'Idea Clarity Session' },
    ]
  },
  {
    label: 'Instruments',
    href: '/instruments',
    children: [
      { href: '/oracle-deck', label: 'The Oracle Deck' },
      { href: '/planner', label: 'Prism Planner' },
    ]
  },
  { label: 'About', href: '/about' },
  { label: 'Contact', href: '/contact' },
];

// Desktop dropdown component
const NavDropdown = ({ group, location }) => {
  const [open, setOpen] = useState(false);
  const timeoutRef = useRef(null);

  const handleEnter = () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setOpen(true);
  };
  const handleLeave = () => {
    timeoutRef.current = setTimeout(() => setOpen(false), 180);
  };

  const isActive = location.pathname === group.href ||
    group.children?.some(c => location.pathname === c.href || (c.href !== '/' && location.pathname.startsWith(c.href)));

  if (!group.children) {
    return (
      <Link
        to={group.href}
        className={`font-['Inter'] text-[13px] font-medium uppercase tracking-[0.14em] transition-colors duration-300 ${
          location.pathname === group.href
            ? 'text-[hsl(var(--gold))]'
            : 'text-[hsl(var(--silver))] hover:text-[hsl(var(--foreground))]'
        }`}
      >
        {group.label}
      </Link>
    );
  }

  return (
    <div className="relative" onMouseEnter={handleEnter} onMouseLeave={handleLeave}>
      <Link
        to={group.href}
        className={`font-['Inter'] text-[13px] font-medium uppercase tracking-[0.14em] transition-colors duration-300 flex items-center gap-1.5 ${
          isActive
            ? 'text-[hsl(var(--gold))]'
            : 'text-[hsl(var(--silver))] hover:text-[hsl(var(--foreground))]'
        }`}
      >
        {group.label}
        <ChevronDown className={`h-3 w-3 transition-transform duration-200 ${open ? 'rotate-180' : ''}`} />
      </Link>

      {/* Dropdown */}
      <div
        className={`absolute top-full left-1/2 -translate-x-1/2 pt-3 transition-all duration-200 ${
          open ? 'opacity-100 translate-y-0 pointer-events-auto' : 'opacity-0 -translate-y-1 pointer-events-none'
        }`}
      >
        <div className="rounded-xl border border-[rgba(201,168,76,0.18)] bg-[hsl(166,24%,11%)] backdrop-blur-md shadow-[0_18px_60px_rgba(0,0,0,0.5)] py-2 min-w-[220px]">
          {group.children.map(child => child.external ? (
            <a
              key={child.href}
              href={child.href}
              target="_blank"
              rel="noopener noreferrer"
              className="block px-5 py-2.5 font-['Inter'] text-[13px] text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] hover:bg-[rgba(27,77,68,0.12)] transition-colors duration-200"
            >
              {child.label}
            </a>
          ) : (
            <Link
              key={child.href}
              to={child.href}
              onClick={() => setOpen(false)}
              className={`block px-5 py-2.5 font-['Inter'] text-[13px] transition-colors duration-200 ${
                location.pathname === child.href || location.pathname.startsWith(child.href + '/')
                  ? 'text-[hsl(var(--gold))] bg-[rgba(27,77,68,0.1)]'
                  : 'text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] hover:bg-[rgba(27,77,68,0.12)]'
              }`}
            >
              {child.label}
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
};

// Mobile accordion group
const MobileNavGroup = ({ group, location, onNavigate }) => {
  const [expanded, setExpanded] = useState(false);

  if (!group.children) {
    return (
      <Link
        to={group.href}
        onClick={onNavigate}
        className={`font-['Cormorant_Garamond'] text-2xl font-semibold transition-colors duration-300 ${
          location.pathname === group.href
            ? 'text-[hsl(var(--gold))]'
            : 'text-[hsl(var(--foreground))] hover:text-[hsl(var(--gold))]'
        }`}
      >
        {group.label}
      </Link>
    );
  }

  const isActive = location.pathname === group.href ||
    group.children?.some(c => location.pathname === c.href || location.pathname.startsWith(c.href + '/'));

  return (
    <div>
      <div className="flex items-center justify-between">
        <Link
          to={group.href}
          onClick={onNavigate}
          className={`font-['Cormorant_Garamond'] text-2xl font-semibold transition-colors duration-300 ${
            isActive ? 'text-[hsl(var(--gold))]' : 'text-[hsl(var(--foreground))] hover:text-[hsl(var(--gold))]'
          }`}
        >
          {group.label}
        </Link>
        <button
          onClick={() => setExpanded(!expanded)}
          className="p-1 text-[hsl(var(--silver))] hover:text-[hsl(var(--foreground))] transition-colors"
        >
          <ChevronDown className={`h-4 w-4 transition-transform duration-200 ${expanded ? 'rotate-180' : ''}`} />
        </button>
      </div>
      {expanded && (
        <div className="mt-2 ml-4 space-y-3 border-l border-[rgba(201,168,76,0.12)] pl-4">
          {group.children.map(child => child.external ? (
            <a
              key={child.href}
              href={child.href}
              target="_blank"
              rel="noopener noreferrer"
              onClick={onNavigate}
              className="block font-['Inter'] text-base text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] transition-colors"
            >
              {child.label}
            </a>
          ) : (
            <Link
              key={child.href}
              to={child.href}
              onClick={onNavigate}
              className={`block font-['Inter'] text-base transition-colors ${
                location.pathname === child.href
                  ? 'text-[hsl(var(--gold))]'
                  : 'text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))]'
              }`}
            >
              {child.label}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
};

export const SiteHeader = () => {
  const location = useLocation();
  const [open, setOpen] = useState(false);

  return (
    <header
      className="sticky top-0 z-50 backdrop-blur-md border-b border-[rgba(201,168,76,0.18)]"
      style={{ background: 'rgba(7,10,10,0.55)' }}
      data-testid="site-header"
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 sm:h-20">
          {/* Wordmark */}
          <Link
            to="/"
            className="font-['Cormorant_Garamond'] text-lg sm:text-xl font-bold tracking-wide text-[hsl(var(--foreground))] hover:text-[hsl(var(--gold))] transition-colors duration-300"
            data-testid="site-header-home-link"
          >
            THE COSMIC SYNTHESIST
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden lg:flex items-center gap-8">
            {NAV_GROUPS.map(group => (
              <NavDropdown key={group.label} group={group} location={location} />
            ))}
          </nav>

          {/* Mobile Nav */}
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild className="lg:hidden">
              <Button variant="ghost" size="icon" className="text-[hsl(var(--silver))] hover:text-[hsl(var(--foreground))]">
                <Menu className="h-5 w-5" />
              </Button>
            </SheetTrigger>
            <SheetContent
              side="right"
              className="w-80 bg-[hsl(166,24%,11%)] border-l border-[rgba(201,168,76,0.18)] p-0"
            >
              <div className="flex flex-col h-full pt-16 px-8">
                <Link
                  to="/"
                  onClick={() => setOpen(false)}
                  className="font-['Cormorant_Garamond'] text-2xl font-semibold text-[hsl(var(--foreground))] hover:text-[hsl(var(--gold))] transition-colors mb-8"
                >
                  Home
                </Link>
                <nav className="flex flex-col gap-5">
                  {NAV_GROUPS.map(group => (
                    <MobileNavGroup
                      key={group.label}
                      group={group}
                      location={location}
                      onNavigate={() => setOpen(false)}
                    />
                  ))}
                </nav>
                <div className="mt-auto pb-8">
                  <Link
                    to="/sacred-feminine-quiz"
                    onClick={() => setOpen(false)}
                    className="block font-['Inter'] text-sm text-[hsl(var(--gold))] hover:text-[hsl(var(--champagne))] transition-colors mb-4"
                  >
                    Take the Archetype Quiz
                  </Link>
                  <Link
                    to="/admin/login"
                    onClick={() => setOpen(false)}
                    className="font-['Inter'] text-xs font-medium uppercase tracking-[0.14em] text-[hsl(var(--silver))] hover:text-[hsl(var(--foreground))] transition-colors"
                  >
                    Admin
                  </Link>
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
};
